import { useEffect, useMemo, useState } from 'react'
import { useParams } from 'react-router-dom'
import { enqueueSnackbar } from 'notistack'
import { Button, Paper, useTheme, Box, Skeleton, Typography, useMediaQuery } from '@mui/material'
import { Notification } from '@components/prereg_validation/notification'
import { useSessionContext } from '@providers/session'
import { buildErrorMessage } from '@shared/error'
import { i18WithParams as t } from '@shared/locale'
import { Spacer } from 'components-ui/src/spacer/spacer'
import { Hooks, Services } from 'service-api'
import { captureException } from 'service-api/src/shared'
import { ReportCard } from 'service-api/src/types/reportCards'
import { CustomError } from 'components-ui/src'

type ReportCardState = {
  id: string
  publicUrl: string
  createdDate: string
  petName: string
}

export const ReportCards = () => {
  const sessionCtx = useSessionContext()
  const { accountName: locationName = '' } = useParams()
  const theme = useTheme()
  const isBelowMD = useMediaQuery(theme.breakpoints.down('md'))
  const lupId = sessionCtx.state.user?.lupId || ''
  const [reportCards, setReportCards] = useState<ReportCardState[]>([])
  const [isLoading, setIsloading] = useState<boolean>(true)

  const { data, isFetching } = Hooks.useClientLocationUserProfile(locationName, lupId || '', ['locationPetProfiles'])

  const petProfiles = useMemo(() => {
    if (data?.locationPetProfiles) {
      return data.locationPetProfiles
    }
    return []
  }, [data?.locationPetProfiles])

  useEffect(() => {
    const fetchReportCards = async () => {
      setIsloading(true)
      try {
        const preparedPromises = petProfiles.map((pet) =>
          Services.ClientReportCardListService.getReportCardListByPetId(locationName, pet.id || '')
        )
        const reportCards = await Promise.allSettled(preparedPromises)

        const mergedCards: ReportCardState[] = reportCards.flatMap((res) => {
          if (res.status === 'fulfilled' && Array.isArray(res.value)) {
            return res.value.map((card: ReportCard) => {
              return {
                id: card.id ?? '',
                publicUrl: card.publicUrl ?? '',
                createdDate: new Date(card.createdAtUtc || 0).toLocaleDateString('en-US'),
                petName: card?.lpp?.displayName || 'Unnamed Pet',
              }
            })
          }
          return []
        })
        // Sort report card by date
        const sortedCards = mergedCards.sort(
          (a, b) => new Date(b.createdDate).getTime() - new Date(a.createdDate).getTime()
        )
        setReportCards(sortedCards)
      } catch (error) {
        enqueueSnackbar(buildErrorMessage(error), { variant: 'error' })
        captureException(new CustomError(buildErrorMessage(error), 'ClientReportCards', { cause: error }), {
          context: { locationName, lupId },
          tags: [{ key: 'errorType', value: 'report card fetch' }],
        })
      }
      setIsloading(false)
    }

    if (petProfiles.length > 0) {
      fetchReportCards()
    }
  }, [locationName, petProfiles, lupId])

  return (
    <Box sx={{ width: '100%' }}>
      <Notification lupId={sessionCtx.state.user?.lupId || ''} />
      <Typography variant="h2" sx={{ mb: 1 }} pl={isBelowMD ? 2 : 0}>
        {t('profile.reportCards.title')}
      </Typography>
      <Typography variant="body1bold" color="text.secondary" sx={{ mb: 2 }} pl={isBelowMD ? 2 : 0}>
        {t('profile.reportCards.subtitle')}
      </Typography>
      <Spacer height={3} />
      {(isLoading || isFetching) && <Skeleton variant="rectangular" width="100%" height={600} />}
      {!isLoading && reportCards.length === 0 && (
        <Paper sx={{ padding: 3 }}>
          <Typography variant="h5" color="text.secondary">
            {t('profile.reportCards.empty')}
          </Typography>
        </Paper>
      )}
      {!isLoading && reportCards.length > 0 && (
        <>
          {reportCards.map((card) => (
            <Paper
              key={card?.id}
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                elevation: 2,
                padding: 1.5,
                mb: 2,
              }}
            >
              <Box>
                <Typography variant="h5">
                  {t('profile.reportCard.label')} - {card.createdDate}
                </Typography>
                <Typography color="text.secondary" variant="body2">
                  {card.petName}
                </Typography>
              </Box>
              <Button
                variant="contained"
                size="medium"
                color="primary"
                onClick={() => window.open(card.publicUrl, '_blank')}
              >
                {t('profile.reportCards.button')}
              </Button>
            </Paper>
          ))}
        </>
      )}
    </Box>
  )
}
